import { useSetToast } from "@app/components/Toast";
import { browserDownload } from "@app/lib/utils-browser";
import { queryClient } from "@app/QueryClientWithHeaders";
import { GraphQLSupplierRepository } from "@app/repositories/GraphQLRepositories/SupplierRepository";
import { questionnairesKeys } from "@app/screens/ExternalQuestionnaires/data";
import { isSupportedLocale } from "@app/shared/utils/locales";
import { type NewS3File } from "@app/usecases/FileUseCases";
import {
    type SupplierTemplateQuestionFilterInput,
    type SupplierTemplateQuestionUpdateInput,
} from "@generated/client/graphql";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supplierKeys } from "../Suppliers/data";

const supplierRepository = new GraphQLSupplierRepository();

export const useSupplierTemplateQuestionnaire = (
    options: {
        pollingInterval?: number;
    } = {},
) => {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.templateQuestionnaire(),
        queryFn: supplierRepository.getSupplierTemplateQuestionnaire,
        ...(options.pollingInterval
            ? {
                  refetchInterval: options.pollingInterval,
              }
            : {}),
    });

    return {
        templateQuestionnaire: data?.supplierTemplate,
        ...query,
    };
};

export const useSupplierTemplateQuestionnaireLocale = () => {
    const { templateQuestionnaire, ...query } =
        useSupplierTemplateQuestionnaire();

    if (!templateQuestionnaire?.locale)
        return {
            availableLocales: undefined,
            defaultLocale: undefined,
            ...query,
        };

    const { availableLocales, default: defaultLocale } =
        templateQuestionnaire.locale;

    const areAvailableLocalesSupported =
        availableLocales.every(isSupportedLocale);
    const isDefaultLocaleSupported = isSupportedLocale(defaultLocale);

    // Necessary controls, available locales are frontend-handled
    if (!isDefaultLocaleSupported) {
        throw new Error("Invalid default locale");
    }
    if (!areAvailableLocalesSupported) {
        throw new Error("Invalid available locales");
    }

    return {
        availableLocales,
        defaultLocale,
        ...query,
    };
};

export const useShouldPollTemplateQuestions = () => {
    const [pollingIntervalOptions, setPollingIntervalOptions] = useState<
        | {
              pollingInterval?: number;
          }
        | undefined
    >(undefined);
    const { templateQuestionnaire, refetch } = useSupplierTemplateQuestionnaire(
        pollingIntervalOptions,
    );

    useEffect(() => {
        const isGenerating = !!templateQuestionnaire?.isGenerating;
        setPollingIntervalOptions(
            isGenerating
                ? {
                      pollingInterval: 1000,
                  }
                : undefined,
        );
    }, [templateQuestionnaire?.isGenerating]);

    /**
     * We need to refetch the template questionnaire
     * on mount, just in case it is used in components
     * that are mounted before the last refetch
     */
    useEffect(() => {
        refetch();
    }, []);

    return !!templateQuestionnaire?.isGenerating;
};

export const useCreateSupplierTemplate = () => {
    const { mutate: createSupplierTemplate, ...mutation } = useMutation({
        mutationFn: supplierRepository.createSupplierTemplate,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionnaire(),
            });
        },
    });

    return {
        createSupplierTemplate,
        ...mutation,
    };
};

export const useUpdateSupplierTemplate = () => {
    const { mutate: updateSupplierTemplate, ...mutation } = useMutation({
        mutationFn: supplierRepository.updateSupplierTemplate,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionnaire(),
            });
        },
    });

    return {
        updateSupplierTemplate,
        ...mutation,
    };
};

export const useUpdateSupplierTemplateQuestion = () => {
    const { mutate: updateQuestion, ...mutation } = useMutation({
        mutationFn: (input: {
            id: string;
            input: SupplierTemplateQuestionUpdateInput;
        }) =>
            supplierRepository.updateSupplierTemplateQuestion(
                input.id,
                input.input,
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
        },
    });

    return {
        updateQuestion,
        ...mutation,
    };
};

export const useUpdateSupplierTemplateQuestions = () => {
    const { mutate: updateQuestions, ...mutation } = useMutation({
        mutationFn: (input: {
            ids: string[];
            input: SupplierTemplateQuestionUpdateInput;
        }) =>
            supplierRepository.updateSupplierTemplateQuestions(
                input.ids,
                input.input,
            ),
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
        },
    });

    return {
        updateQuestions,
        ...mutation,
    };
};

export const useTemplateQuestions = (
    filter: SupplierTemplateQuestionFilterInput = {},
    pagination: {
        skip?: number;
        take?: number;
    } = {},
    options: {
        pollingInterval?: number;
    } = {},
) => {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.templateQuestionsList(filter, pagination),
        queryFn: () =>
            supplierRepository.getTemplateQuestions(filter, pagination),
        ...(options.pollingInterval
            ? {
                  refetchInterval: options.pollingInterval,
              }
            : {}),
    });

    return {
        templateQuestions: data?.supplierTemplateQuestions ?? {
            list: [],
            count: 0,
        },
        ...query,
    };
};

export const useTemplateQuestionGroups = () => {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.templateQuestionGroups(),
        queryFn: supplierRepository.getTemplateQuestionGroups,
    });

    const templateQuestionGroups = useMemo(() => {
        return data?.supplierTemplateQuestionGroups ?? [];
    }, [data]);

    return {
        templateQuestionGroups,
        ...query,
    };
};

export const useExportTemplateQuestions = () => {
    const { mutate: exportTemplateQuestions, ...mutation } = useMutation({
        mutationFn: supplierRepository.exportTemplateQuestions,
        onSettled: (data) => {
            if (data?.exportTemplateQuestions?.data?.signedUrl) {
                browserDownload(data.exportTemplateQuestions.data.signedUrl);
            }
        },
    });

    return { exportTemplateQuestions, ...mutation };
};

export const useTemplateQuestionsMinimal = (
    filter: SupplierTemplateQuestionFilterInput = {},
    pagination: {
        skip?: number;
        take?: number;
    } = {},
    options: {
        pollingInterval?: number;
    } = {},
) => {
    const { data, ...query } = useQuery({
        queryKey: supplierKeys.templateQuestionsListMinimal(filter, pagination),
        queryFn: () =>
            supplierRepository.getTemplateQuestionsMinimal(filter, pagination),
        ...(options.pollingInterval
            ? {
                  refetchInterval: options.pollingInterval,
              }
            : {}),
    });

    return {
        templateQuestionsMinimal: data?.supplierTemplateQuestions ?? {
            list: [],
            count: 0,
        },
        ...query,
    };
};

export const useCreateTemplateQuestion = () => {
    const { mutate: createQuestion, ...mutation } = useMutation({
        mutationFn: supplierRepository.createSupplierTemplateQuestion,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
            // we need this because the question create modal is using some queries from the external questionnaires module
            queryClient.invalidateQueries({
                queryKey: questionnairesKeys.all,
            });
        },
    });

    return {
        createQuestion,
        ...mutation,
    };
};

export const useCreateSupplierTemplateQuestionGroup = () => {
    const { mutate: createQuestionGroup, ...mutation } = useMutation({
        mutationFn: supplierRepository.createSupplierTemplateQuestionGroup,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
        },
    });

    return { createQuestionGroup, ...mutation };
};

export const useUpdateSupplierTemplateQuestionsOrder = () => {
    const { mutate: updateQuestionsOrder, ...mutation } = useMutation({
        mutationFn: supplierRepository.updateSupplierTemplateQuestionsOrder,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
            // we need this because the question create modal is using some queries from the external questionnaires module
            queryClient.invalidateQueries({
                queryKey: questionnairesKeys.all,
            });
        },
    });

    return {
        updateQuestionsOrder,
        ...mutation,
    };
};

export const useUpdateSupplierTemplateQuestionGroup = () => {
    const { mutate: updateQuestionGroup, ...mutation } = useMutation({
        mutationFn: supplierRepository.updateSupplierTemplateQuestionGroup,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
        },
    });

    return { updateQuestionGroup, ...mutation };
};

export const useDeleteTemplateQuestion = () => {
    const { mutate: deleteQuestion, ...mutation } = useMutation({
        mutationFn: supplierRepository.deleteSupplierTemplateQuestion,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
            // we need this because the question create modal is using some queries from the external questionnaires module
            queryClient.invalidateQueries({
                queryKey: questionnairesKeys.all,
            });
        },
    });

    return {
        deleteQuestion,
        ...mutation,
    };
};

export const useDeleteTemplateQuestions = () => {
    const { mutate: deleteQuestions, ...mutation } = useMutation({
        mutationFn: supplierRepository.deleteSupplierTemplateQuestions,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
            // we need this because the question create modal is using some queries from the external questionnaires module
            queryClient.invalidateQueries({
                queryKey: questionnairesKeys.all,
            });
        },
    });

    return {
        deleteQuestions,
        ...mutation,
    };
};

export const useDeleteSupplierTemplateQuestionGroup = () => {
    const { mutate: deleteQuestionGroup, ...mutation } = useMutation({
        mutationFn: supplierRepository.deleteSupplierTemplateQuestionGroup,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierKeys.templateQuestionsListAll(),
            });
        },
    });

    return { deleteQuestionGroup, ...mutation };
};

export const useDownloadQuestionsCreationTemplate = () => {
    const { mutate: downloadQuestionsCreationTemplate, ...mutation } =
        useMutation({
            mutationFn: supplierRepository.downloadQuestionsCreationTemplate,
            onSettled: (data) => {
                if (data?.downloadQuestionsCreationTemplate?.data?.signedUrl) {
                    browserDownload(
                        data.downloadQuestionsCreationTemplate.data.signedUrl,
                    );
                }
            },
        });

    return { downloadQuestionsCreationTemplate, ...mutation };
};

export const useCreateQuestionsFromFile = () => {
    const queryClient = useQueryClient();
    const { setToastError } = useSetToast();

    const { mutateAsync: createQuestionsFromFileAsync, ...mutation } =
        useMutation({
            mutationFn: ({
                s3File: { signedUrl: _, ...s3File },
            }: {
                s3File: NewS3File;
            }) =>
                supplierRepository.createSupplierQuestionsFromFile({
                    s3File,
                    tagIds: [],
                }),
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: supplierKeys.questionsAll(),
                });
            },
            onError: () => {
                setToastError("Error creating questions from file");
            },
        });

    return { createQuestionsFromFileAsync, ...mutation };
};
